import {Button, Popover, Stack, TextField} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FLOATING_SHADOW} from "../../newVariablePicker/shared/floatingShadow";
import {linkHref} from "./linkHref";

interface Props {
    anchorEl: HTMLElement;
    /** The link the cursor is in, if any: the field starts with it, and Remove shows. */
    href?: string;
    onApply: (href: string) => void;
    onRemove: () => void;
    onClose: () => void;
}

function LinkForm({href, onApply, onRemove, onClose}: Omit<Props, 'anchorEl'>) {
    const {t} = useTranslation();
    const [text, setText] = useState(href ?? '');
    const [invalid, setInvalid] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    /*
        Not `autoFocus`: in development StrictMode remounts the Popover's focus trap, whose cleanup
        hands focus back to the editor and whose remount parks it on the Paper, so typing went into the
        text. This effect runs before the trap's on both mounts, and the trap leaves focus here.
     */
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const apply = () => {
        if (!text.trim()) {
            if (href) onRemove(); else onClose();
            return;
        }

        const next = linkHref(text);
        if (next) onApply(next); else setInvalid(true);
    };

    return (
        <Stack
            component={'form'}
            direction={'row'}
            alignItems={'flex-start'}
            gap={1}
            sx={{p: 1.5}}
            onSubmit={event => {
                event.preventDefault();
                apply();
            }}
        >
            <TextField
                size={'small'}
                inputRef={inputRef}
                placeholder={'https://…'}
                value={text}
                onChange={event => {
                    setText(event.target.value);
                    setInvalid(false);
                }}
                error={invalid}
                helperText={invalid ? t('Enter a valid link.') : undefined}
                inputProps={{'aria-label': t('Link')}}
                sx={{width: 260}}
            />
            <Button type={'submit'} variant={'contained'} sx={{flexShrink: 0, height: 40}}>
                {t('Apply')}
            </Button>
            {!!href && (
                <Button color={'error'} onClick={onRemove} sx={{flexShrink: 0, height: 40}}>
                    {t('Remove')}
                </Button>
            )}
        </Stack>
    );
}

/** Mounted only while open, so every opening starts from the link under the cursor. */
export default function LinkPopover({anchorEl, ...form}: Props) {
    return (
        <Popover
            open
            anchorEl={anchorEl}
            onClose={form.onClose}
            transitionDuration={0}
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            slotProps={{paper: {variant: 'outlined', sx: {mt: '8px', borderRadius: 2, boxShadow: FLOATING_SHADOW}}}}
        >
            <LinkForm {...form}/>
        </Popover>
    );
}
